import { Address } from '@bybeam/platform-types';
import { datadogLogs } from '@datadog/browser-logs';
import env from '@platform-ui/utilities/env.js';
import { useCallback } from 'react';

export interface HereAddress {
  label: string;
  countryCode: string;
  countryName: string;
  stateCode: string;
  state: string;
  county?: string;
  city: string;
  district?: string;
  street: string;
  postalCode: string;
  houseNumber?: string;
}

export interface GeocoderResult {
  title: string;
  id: string;
  resultType: string;
  address: HereAddress;
  scoring: { queryScore: number };
  position: { lat: number; lng: number };
}

export interface FormAddress {
  address: string;
  apartment: string;
  city: string;
  county: string;
  zip: string;
  state: string;
}

export interface GeocoderFilter {
  state: string;
  city?: string;
}

type ServiceFunction = (
  search: string,
  addressType?: 'house' | 'city',
  filter?: GeocoderFilter,
) => Promise<GeocoderResult[]>;

interface UseGeocoderReturn {
  autocomplete: ServiceFunction;
  addressIsValid: (search: Partial<FormAddress>) => Promise<GeocoderResult | false>;
  reverseGeocode: (latitude: number, longitude: number) => Promise<GeocoderResult>;
  validateAddress: (
    manualAddress: Omit<Address, 'id' | 'createdAt'>,
  ) => Promise<Omit<Address, 'id' | 'createdAt'>>;
}

const {
  REACT_APP_HERE_API_KEY,
  REACT_APP_HERE_GEOCODE_URL,
  REACT_APP_HERE_AUTOCOMPLETE_URL,
  REACT_APP_HERE_REV_GEOCODE_URL,
} = env() || {};

const getAuthorizedURL = (serviceURL: string): string => {
  if (!serviceURL) {
    datadogLogs.logger.error('Missing env variable for API URL', {
      name: 'Geocoder - Empty Api Url',
    });

    throw new Error('Missing env variable for API URL');
  }
  if (!REACT_APP_HERE_API_KEY) {
    datadogLogs.logger.error('Missing env variable REACT_APP_HERE_API_KEY', {
      name: 'Geocoder - Empty Api Key',
    });

    throw new Error('Missing env variable REACT_APP_HERE_API_KEY');
  }
  return `${serviceURL}?apiKey=${REACT_APP_HERE_API_KEY}`;
};
const COUNTRY_CODE = 'USA';

const useGeocoder = (): UseGeocoderReturn => {
  const geocoder = useCallback(async (search: string): Promise<GeocoderResult[]> => {
    try {
      const geocoderURL = getAuthorizedURL(REACT_APP_HERE_GEOCODE_URL);
      const url = `${geocoderURL}&in=countryCode%3A${COUNTRY_CODE}&q=${encodeURIComponent(search)}`;
      const result = await (await fetch(url)).json();

      return result?.items;
    } catch (e) {
      return [];
    }
  }, []);

  const addressIsValid = useCallback(
    async (addressFormData: Partial<FormAddress>): Promise<GeocoderResult | false> => {
      try {
        const { address, city, state, zip } = addressFormData;
        const search = `${address}, ${city}, ${state}, United States, ${zip}`;
        const results = await geocoder(search);
        return results.find(
          (result) =>
            result.resultType !== 'postalCodePoint' && result.scoring.queryScore >= 8.5 / 10,
        );
      } catch (e) {
        return false;
      }
    },
    [geocoder],
  );

  const autocomplete = useCallback(
    async (
      search: string,
      addressType?: 'house' | 'city',
      filter?: GeocoderFilter,
    ): Promise<GeocoderResult[]> => {
      try {
        const autocompleteURL = getAuthorizedURL(REACT_APP_HERE_AUTOCOMPLETE_URL);
        let url = `${autocompleteURL}&in=countryCode%3A${COUNTRY_CODE}&q=${encodeURIComponent(
          [search, filter?.city, filter?.state].filter(Boolean).join(' '),
        )}`;
        if (addressType === 'city') url = `${url}&types=city`;

        const result = await (await fetch(url)).json();

        let items: GeocoderResult[] = result?.items ?? [];
        if (addressType === 'house')
          items = items.filter((result) => result.resultType === 'houseNumber');
        if (filter) items = items.filter((result) => result.address.stateCode === filter.state);
        if (filter?.city)
          items = items.filter(
            (result) => result.address.city.toLowerCase() === filter.city.toLowerCase(),
          );
        return items;
      } catch (e) {
        return [];
      }
    },
    [],
  );

  const reverseGeocode = useCallback(
    async (latitude: number, longitude: number): Promise<GeocoderResult> => {
      try {
        const revGeocodeURL = getAuthorizedURL(REACT_APP_HERE_REV_GEOCODE_URL);
        const position = `${latitude},${longitude}`;
        const url = `${revGeocodeURL}&at=${encodeURIComponent(position)}`;
        const result = await (await fetch(url)).json();

        return result?.items[0];
      } catch (e) {
        return null;
      }
    },
    [],
  );

  const validateAddress = useCallback(
    async (
      manualAddress: Omit<Address, 'id' | 'createdAt'>,
    ): Promise<Omit<Address, 'id' | 'createdAt'>> => {
      try {
        const { addressLine1, addressLine2, city, state, zip } = manualAddress;

        const addressToValidate = {
          address: addressLine1,
          apartment: addressLine2,
          city,
          state,
          zip,
        };

        const geocodeAddr = await addressIsValid(addressToValidate);

        if (!geocodeAddr) {
          return manualAddress;
        }

        const { address } = geocodeAddr;

        return {
          city: address.city,
          state: address.stateCode,
          zip: address.postalCode,
          addressLine1: addressLine1
            ? [address.houseNumber, address.street].filter(Boolean).join(' ')
            : '',
          addressLine2: addressLine2 ?? '',
          county: address.county,
          latitude: geocodeAddr.position.lat,
          longitude: geocodeAddr.position.lng,
        };
      } catch (e) {
        datadogLogs.logger.error('validateAddress: unexpected error >', {
          name: 'Geocoder - ValidateAddress error',
          error: e,
        });
        return manualAddress;
      }
    },
    [addressIsValid],
  );

  return {
    autocomplete,
    addressIsValid,
    reverseGeocode,
    validateAddress,
  };
};

export default useGeocoder;
